import React, { useCallback, useEffect, useMemo, useState } from 'react';
import MediaAnnotator from '@/components/MediaAnnotator';
import {
  CheckCircle2, RotateCcw, MessageSquare, Inbox, Film, User, Clock,
  Send, X, ChevronDown,
} from 'lucide-react';

/**
 * LineupReviewPanel
 * =================
 * Founder ask Jun 24, 2026: clients should see the full episode lineup
 * as a single-window player with annotation + frame-by-frame controls
 * (like the legacy ANIMATICS review). This is distinct from the
 * SyncSketch-style side-by-side `CompareView` which is for internal
 * shot-by-shot QA.
 *
 * Props
 * -----
 *   submissions        — array of submissions for the animation dept
 *                         (shot AND episode_lineup mixed). We pick the
 *                         latest kind='episode_lineup' as the active.
 *   api                — axios instance from useAuth
 *   mediaUrlFor        — auth-aware blob resolver (resolveMediaUrl)
 *   onDecide           — (submission_id, decision, note) → promise
 *   user               — current user (drives readOnly + role tagging)
 *
 * The panel renders:
 *   • Header — project / version + "View as client" toggle help
 *   • Left  — large MediaAnnotator
 *   • Right — annotation list + decision buttons (approve / changes)
 *
 * Annotation persistence uses the SAME endpoints as the legacy
 * detail view (POST /reviews/:id/annotations). No backend changes
 * needed. Decisions go through the parent-provided onDecide().
 */
export default function LineupReviewPanel({
  submissions = [],
  api,
  mediaUrlFor,
  onDecide,
  user,
}) {
  // Pick the latest episode_lineup submission (highest version, or
  // newest if all v1). The CompareView gets shot submissions; this
  // panel only cares about the full lineup file.
  const lineupSub = useMemo(() => {
    const lineups = (submissions || [])
      .filter(s => s.kind === 'episode_lineup')
      .sort((a, b) => {
        const va = Number(a.version || 0);
        const vb = Number(b.version || 0);
        if (va !== vb) return vb - va;
        return new Date(b.created_at || 0).getTime() - new Date(a.created_at || 0).getTime();
      });
    return lineups[0] || null;
  }, [submissions]);

  const [detail, setDetail] = useState(null);
  const [mediaIdx, setMediaIdx] = useState(0);
  const [mediaUrls, setMediaUrls] = useState({}); // idx -> blob url
  const [decisionNote, setDecisionNote] = useState('');
  const [busy, setBusy] = useState(false);
  const [activeAnnId, setActiveAnnId] = useState(null);
  const [seekTick, setSeekTick] = useState(0);
  const [err, setErr] = useState('');
  // Founder spec Jun 24 2026 — Phase 2: forward lineup notes to shots.
  // Cache the project's shot-submission list once for the dropdown.
  const [shotList, setShotList] = useState([]);
  const [shotListLoaded, setShotListLoaded] = useState(false);
  const [forwardingAnn, setForwardingAnn] = useState(null); // ann.id whose
                                                              // forward menu is open
  const [forwardBusy, setForwardBusy] = useState(false);
  const [forwardSuccess, setForwardSuccess] = useState({}); // ann.id -> label

  // Auto-load the lineup submission's detail (annotations, media list,
  // decision history) when the active lineup changes.
  useEffect(() => {
    if (!lineupSub?.id) { setDetail(null); return; }
    let mounted = true;
    setErr('');
    (async () => {
      try {
        const { data } = await api.get(`/reviews/${lineupSub.id}`);
        if (mounted) setDetail(data);
      } catch (e) {
        if (mounted) setErr(e?.response?.data?.detail || e.message || 'Failed to load lineup.');
      }
    })();
    return () => { mounted = false; };
  }, [api, lineupSub?.id]);

  // Resolve media blob URLs for each media item (lineup typically has
  // just one MP4 but we handle the multi-media case the same as
  // ClientReviewPage's detail view).
  useEffect(() => {
    if (!detail?.submission?.media) return;
    let cancelled = false;
    (async () => {
      const next = {};
      for (let i = 0; i < detail.submission.media.length; i++) {
        try {
          const url = await mediaUrlFor(detail.submission.media[i]);
          if (cancelled) return;
          if (url) next[i] = url;
        } catch (_e) { /* tolerate per-item failure */ }
      }
      if (!cancelled) setMediaUrls(prev => ({ ...prev, ...next }));
    })();
    return () => { cancelled = true; };
  }, [detail?.submission?.media, mediaUrlFor]);

  // Annotation CRUD — same endpoints as ClientReviewPage's detail view.
  const addAnnotation = useCallback(async (payload) => {
    if (!lineupSub?.id) return null;
    try {
      const { data } = await api.post(`/reviews/${lineupSub.id}/annotations`,
        { media_index: mediaIdx, ...payload });
      // Re-fetch detail so the new annotation appears immediately.
      setDetail(d => d ? { ...d, annotations: [...(d.annotations || []), data] } : d);
      return data;
    } catch (e) {
      setErr(e?.response?.data?.detail || e.message || 'Failed to add note.');
      return null;
    }
  }, [api, lineupSub?.id, mediaIdx]);

  const resolveAnnotation = useCallback(async (id) => {
    if (!lineupSub?.id) return;
    try {
      await api.post(`/reviews/${lineupSub.id}/annotations/${id}/resolve`);
      setDetail(d => d ? {
        ...d,
        annotations: (d.annotations || []).map(a =>
          a.id === id ? { ...a, resolved: true } : a),
      } : d);
    } catch (e) {
      setErr(e?.response?.data?.detail || e.message || 'Failed to resolve note.');
    }
  }, [api, lineupSub?.id]);

  const deleteAnnotation = useCallback(async (id) => {
    if (!lineupSub?.id) return;
    try {
      await api.delete(`/reviews/${lineupSub.id}/annotations/${id}`);
      setDetail(d => d ? {
        ...d,
        annotations: (d.annotations || []).filter(a => a.id !== id),
      } : d);
    } catch (e) {
      setErr(e?.response?.data?.detail || e.message || 'Failed to delete note.');
    }
  }, [api, lineupSub?.id]);

  // ── Phase 2: shot list + forward ─────────────────────────────────
  // Lazy-load the project's shot submissions the first time the user
  // opens the forward menu. Cached for the rest of the session.
  const ensureShotList = useCallback(async () => {
    if (shotListLoaded || !lineupSub?.project_id) return;
    try {
      const { data } = await api.get(
        `/reviews/projects/${lineupSub.project_id}/shot-submissions`);
      setShotList(data?.shots || []);
    } catch (e) {
      // Non-fatal: forward UI just shows "no shots available".
      console.warn('Failed to load shot list for forwarding:', e);
    } finally {
      setShotListLoaded(true);
    }
  }, [api, lineupSub?.project_id, lineupSub?.id, shotListLoaded]);

  const forwardToShot = useCallback(async (annId, targetSubmissionId, label) => {
    setForwardBusy(true);
    try {
      await api.post(`/reviews/annotations/${annId}/forward-to-shot`,
        { target_submission_id: targetSubmissionId });
      setForwardSuccess(prev => ({ ...prev, [annId]: label }));
      setForwardingAnn(null);
    } catch (e) {
      setErr(e?.response?.data?.detail || e.message || 'Forward failed.');
    } finally {
      setForwardBusy(false);
    }
  }, [api]);

  // Decision (approve / request changes) — delegates to parent.
  const decide = useCallback(async (decision) => {
    if (!lineupSub?.id || !onDecide) return;
    setBusy(true);
    try {
      await onDecide(lineupSub.id, decision, decisionNote);
      setDecisionNote('');
    } catch (e) {
      setErr(e?.response?.data?.detail || e.message || 'Decision failed.');
    } finally {
      setBusy(false);
    }
  }, [lineupSub?.id, onDecide, decisionNote]);

  // ── Empty state — no lineup uploaded yet ──
  if (!lineupSub) {
    return (
      <div className="h-full flex flex-col items-center justify-center text-center px-8 py-16"
            data-testid="lineup-empty">
        <Inbox className="h-12 w-12 text-amber-400/60 mb-4" />
        <h3 className="text-lg font-semibold text-zinc-200 mb-2">
          No full lineup uploaded yet
        </h3>
        <p className="text-sm text-zinc-500 max-w-md">
          Your Production Manager will send the full episode lineup
          shortly. Once uploaded, the lineup will appear here for
          your review — you&apos;ll be able to scrub frame-by-frame,
          draw on top of any frame, and leave time-stamped notes.
        </p>
      </div>
    );
  }

  // Detail still loading
  if (!detail) {
    return (
      <div className="h-full flex items-center justify-center text-sm text-zinc-500"
            data-testid="lineup-loading">
        {err ? (
          <div className="text-red-400">{err}</div>
        ) : (
          <>Loading lineup…</>
        )}
      </div>
    );
  }

  const sub = detail.submission || lineupSub;
  const annotations = (detail.annotations || []).filter(a => a.media_index === mediaIdx);
  const media = sub.media || [];
  const m = media[mediaIdx];
  const url = mediaUrls[mediaIdx];
  // Frontend role check — for now only the SUBMITTER can't add notes;
  // everyone else (client + internal preview) can annotate freely.
  const isOwnSubmission = sub.submitted_by === user?.user_id;
  const readOnly = isOwnSubmission && user?.role && !['client', 'master_admin'].includes(user.role);
  const pendingDecision = sub.status === 'pending' || sub.status === 'pending_review' || sub.status === 'in_review';

  return (
    <div className="flex h-full" data-testid="lineup-review-panel">
      {/* Left — Player */}
      <div className="flex-1 flex flex-col overflow-hidden">
        {/* Header */}
        <div className="border-b border-zinc-800 bg-zinc-950 px-5 py-3 flex items-center justify-between">
          <div className="flex items-center gap-3">
            <Film className="h-5 w-5 text-amber-400" />
            <div>
              <div className="text-sm font-semibold text-zinc-100">
                {sub.title || 'Episode Lineup'}
              </div>
              <div className="text-[11px] text-zinc-500 flex items-center gap-3 mt-0.5">
                <span className="flex items-center gap-1">
                  <User className="h-3 w-3" />
                  {sub.submitter_name || sub.submitted_by}
                </span>
                <span className="flex items-center gap-1">
                  <Clock className="h-3 w-3" />
                  v{sub.version || 1}
                </span>
                {sub.episode_label && (
                  <span className="text-amber-400/80">{sub.episode_label}</span>
                )}
              </div>
            </div>
          </div>
          {err && (
            <div className="text-xs text-red-400 max-w-md truncate" title={err}>
              {err}
            </div>
          )}
        </div>
        {/* Player area */}
        <div className="flex-1 overflow-auto bg-black flex items-center justify-center p-4">
          {url ? (
            <MediaAnnotator
              src={url}
              type={m?.type || 'video'}
              annotations={annotations}
              onAdd={readOnly ? null : addAnnotation}
              onResolve={readOnly ? null : resolveAnnotation}
              onDelete={readOnly ? null : deleteAnnotation}
              readOnly={readOnly}
              viewerRole={user?.role}
              activeAnnotationId={activeAnnId}
              seekTick={seekTick}
              onActiveAnnotationChange={(id) => {
                setActiveAnnId(id);
                if (id) setSeekTick(t => t + 1);
              }}
            />
          ) : (
            <div className="h-72 flex items-center justify-center text-sm text-zinc-500">
              Loading media…
            </div>
          )}
        </div>
        {/* Footer hint — frame-by-frame keys */}
        <div className="border-t border-zinc-800 bg-zinc-950 px-5 py-2 flex items-center justify-between text-[11px] text-zinc-500">
          <div className="flex items-center gap-3">
            <kbd className="bg-zinc-800 px-1.5 py-0.5 rounded text-zinc-300">Space</kbd>
            <span>play/pause</span>
            <kbd className="bg-zinc-800 px-1.5 py-0.5 rounded text-zinc-300">←</kbd>
            <kbd className="bg-zinc-800 px-1.5 py-0.5 rounded text-zinc-300">→</kbd>
            <span>frame ±1</span>
            <kbd className="bg-zinc-800 px-1.5 py-0.5 rounded text-zinc-300">J</kbd>
            <kbd className="bg-zinc-800 px-1.5 py-0.5 rounded text-zinc-300">L</kbd>
            <span>±5s</span>
          </div>
          <div className="text-zinc-600">
            Click any frame to drop a pin / draw a note
          </div>
        </div>
      </div>

      {/* Right — Annotations + Decision */}
      <aside className="w-96 border-l border-zinc-800 bg-zinc-950 flex flex-col overflow-hidden">
        <div className="border-b border-zinc-800 px-4 py-3 flex items-center gap-2">
          <MessageSquare className="h-4 w-4 text-amber-400" />
          <span className="text-sm font-semibold text-zinc-100">
            Notes ({annotations.length})
          </span>
        </div>
        <div className="flex-1 overflow-y-auto px-3 py-3 space-y-2"
              data-testid="lineup-annotation-list">
          {annotations.length === 0 ? (
            <div className="text-xs text-zinc-500 text-center py-12">
              No notes yet. Click anywhere on the video to drop a pin
              or use the toolbar to draw on a frame.
            </div>
          ) : (
            annotations
              .sort((a, b) => (a.timecode_sec || 0) - (b.timecode_sec || 0))
              .map(a => {
                const t = a.timecode_sec || 0;
                const mm = Math.floor(t / 60);
                const ss = (t % 60).toFixed(2).padStart(5, '0');
                const isOpen = forwardingAnn === a.id;
                return (
                  <div
                    key={a.id}
                    className={`rounded border text-xs transition ${
                      activeAnnId === a.id
                        ? 'border-amber-500 bg-amber-500/10'
                        : 'border-zinc-800 bg-zinc-900/50'
                    } ${a.resolved ? 'opacity-50' : ''}`}
                    data-testid={`lineup-note-${a.id}`}
                  >
                    <button
                      onClick={() => { setActiveAnnId(a.id); setSeekTick(t => t + 1); }}
                      className="w-full text-left p-2.5 hover:bg-zinc-800/40 rounded-t"
                    >
                      <div className="flex items-center justify-between mb-1">
                        <span className="font-mono text-amber-400">
                          {mm}:{ss}
                        </span>
                        {a.resolved && (
                          <span className="text-[10px] text-emerald-400">
                            ✓ resolved
                          </span>
                        )}
                      </div>
                      <p className="text-zinc-200 whitespace-pre-wrap break-words">
                        {a.text || a.note || '(no text)'}
                      </p>
                      <div className="text-[10px] text-zinc-500 mt-1">
                        {a.author_name || a.created_by_name || 'Reviewer'}
                      </div>
                    </button>

                    {/* Phase 2 — forward-to-shot toolbar */}
                    <div className="border-t border-zinc-800 px-2 py-1.5 flex items-center justify-between gap-2">
                      {forwardSuccess[a.id] ? (
                        <span className="text-[10px] text-emerald-400 flex items-center gap-1"
                                data-testid={`lineup-note-forward-success-${a.id}`}>
                          <CheckCircle2 className="h-3 w-3" />
                          Sent to {forwardSuccess[a.id]}
                        </span>
                      ) : (
                        <>
                          <button
                            onClick={(e) => {
                              e.stopPropagation();
                              setForwardingAnn(isOpen ? null : a.id);
                              if (!isOpen) ensureShotList();
                            }}
                            className="text-[10px] text-zinc-400 hover:text-amber-400 flex items-center gap-1"
                            data-testid={`lineup-note-forward-btn-${a.id}`}
                          >
                            <Send className="h-3 w-3" />
                            {isOpen ? 'Cancel' : 'Send to shot…'}
                          </button>
                          {!a.resolved && (
                            <button
                              onClick={() => resolveAnnotation(a.id)}
                              className="text-[10px] text-zinc-500 hover:text-emerald-400"
                              data-testid={`lineup-note-resolve-${a.id}`}
                            >
                              Resolve
                            </button>
                          )}
                        </>
                      )}
                    </div>

                    {/* Shot dropdown when forwarding */}
                    {isOpen && (
                      <div className="border-t border-zinc-800 bg-zinc-950 p-2 max-h-48 overflow-y-auto"
                            data-testid={`lineup-note-forward-menu-${a.id}`}>
                        {!shotListLoaded ? (
                          <div className="text-[10px] text-zinc-500 px-1 py-2">
                            Loading shots…
                          </div>
                        ) : shotList.length === 0 ? (
                          <div className="text-[10px] text-zinc-500 px-1 py-2 leading-relaxed">
                            No shot submissions yet for this project.
                            Forward once the artists have uploaded
                            their playblasts.
                          </div>
                        ) : (
                          <div className="space-y-0.5">
                            <div className="text-[9px] uppercase tracking-wider text-zinc-500 px-1 mb-1">
                              Pick destination shot
                            </div>
                            {shotList.map(s => (
                              <button
                                key={s.submission_id}
                                onClick={() => forwardToShot(
                                  a.id, s.submission_id, s.label)}
                                disabled={forwardBusy}
                                className="w-full text-left px-2 py-1.5 rounded hover:bg-zinc-800 text-[11px] text-zinc-200 flex items-center justify-between disabled:opacity-50"
                                data-testid={`lineup-note-forward-target-${s.submission_id}`}
                              >
                                <span className="font-mono">{s.label}</span>
                                <span className="text-[10px] text-zinc-500">
                                  v{s.version || 1} · {s.submitter_name || '—'}
                                </span>
                              </button>
                            ))}
                          </div>
                        )}
                      </div>
                    )}
                  </div>
                );
              })
          )}
        </div>

        {/* Decision bar — only on pending lineups + when reviewer is not the submitter */}
        {pendingDecision && !isOwnSubmission && onDecide && (
          <div className="border-t border-zinc-800 px-4 py-3 space-y-2"
                data-testid="lineup-decision-bar">
            <textarea
              value={decisionNote}
              onChange={(e) => setDecisionNote(e.target.value)}
              placeholder="Decision note (optional) — visible to the artist/PM."
              rows={2}
              className="w-full text-xs bg-zinc-900 border border-zinc-800 rounded px-2 py-1.5 text-zinc-100 placeholder-zinc-500"
            />
            <div className="grid grid-cols-2 gap-2">
              <button
                disabled={busy}
                onClick={() => decide('changes_requested')}
                className="flex items-center justify-center gap-1.5 px-3 py-2 rounded text-xs font-semibold bg-amber-500/10 text-amber-400 border border-amber-500/30 hover:bg-amber-500/20 disabled:opacity-50"
                data-testid="lineup-request-changes-btn"
              >
                <RotateCcw className="h-3.5 w-3.5" />
                Request Changes
              </button>
              <button
                disabled={busy}
                onClick={() => decide('approved')}
                className="flex items-center justify-center gap-1.5 px-3 py-2 rounded text-xs font-semibold bg-emerald-500/10 text-emerald-400 border border-emerald-500/30 hover:bg-emerald-500/20 disabled:opacity-50"
                data-testid="lineup-approve-btn"
              >
                <CheckCircle2 className="h-3.5 w-3.5" />
                Approve Lineup
              </button>
            </div>
          </div>
        )}
      </aside>
    </div>
  );
}
